import { db } from "../firebase-config";
import { collection, doc, deleteDoc, setDoc, addDoc, getDocs, getDoc, onSnapshot, query, runTransaction, Timestamp } from "firebase/firestore";

// Add a new inventory item
export const addInventoryItem = async (organization, item) => {
    try {
        await addDoc(collection(db, "organizations", organization, "inventory"), { ...item });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error adding inventory item:", error);
        return { success: false, error: error.message || 'Failed to add inventory item' };
    }
};

// Get a single inventory item by ID
export const getInventoryItemById = async (organization, itemId) => {
    const itemRef = doc(db, 'organizations', organization, 'inventory', itemId);
    const itemSnapshot = await getDoc(itemRef);
  
    if (itemSnapshot.exists()) {
      return { id: itemSnapshot.id, ...itemSnapshot.data() };
    } else {
      return null;
    }
  };

// Real-time listener for a single inventory item
export const inventoryItemSnapshotListener = (organization, itemId, onItemUpdate) => {
    const itemRef = doc(db, 'organizations', organization, 'inventory', itemId);
    const unsubscribe = onSnapshot(itemRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
            const org = { id: docSnapshot.id, ...docSnapshot.data() };
            onItemUpdate(org);
        } else {
            onItemUpdate(null);
        }
    })
    return unsubscribe;
  };

// Real-time listener for inventory history
export const inventoryHistorySnapshotListener = (organizationId, itemId, onHistoryUpdate) => {
    const historyQuery = query(collection(db, 'organizations', organizationId, 'inventory', itemId, 'inventory_history'));
    
    const unsubscribe = onSnapshot(historyQuery, (snapshot) => {
        const history = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        onHistoryUpdate(history);
    });

    return unsubscribe;  
};

// Get all inventory items
export const getInventoryItems = async (organization) => {
    const itemsCollectionRef = collection(db, "organizations", organization, "inventory");
    const querySnapshot = await getDocs(itemsCollectionRef);
    const inventoryItems = [];
    querySnapshot.forEach((doc) => {
        inventoryItems.push({ id: doc.id, ...doc.data() });
    });
    return inventoryItems;
};



export const deleteInventoryItem = async (organization, itemId) => {
    try {
        const itemRef = doc(db, "organizations", organization, "inventory", itemId);
        await deleteDoc(itemRef);
        return { success: true, error: null };
    } catch (error) {
        console.error("Error deleting inventory item:", error);
        return { success: false, error: error.message || 'Failed to delete inventory item' };
    }
};


// Update an inventory item
export const updateInventoryItem = async (organization, itemId, itemUpdate) => {
    try {
        await setDoc(doc(db, "organizations", organization, "inventory", itemId), itemUpdate, { merge: true });
        return { success: true, error: null };
    } catch (error) {
        return { success: false, error: error.message || 'Failed to update inventory item' };
    }
};

// Adjust an inventory item with a transaction
export const adjustInventoryItem = async (organization, itemId, adjustmentAmount, adjustmentType, reason, notes, userId) => {
    const itemRef = doc(db, "organizations", organization, "inventory", itemId);

    try {
        await runTransaction(db, async (transaction) => {
            const itemSnapshot = await transaction.get(itemRef);

            if (!itemSnapshot.exists()) {
                throw new Error("Inventory item not found.");
            }

            const itemData = itemSnapshot.data();
            const currentQuantity = itemData.quantity || 0;

            let newQuantity = adjustmentType === "increase"
                ? currentQuantity + adjustmentAmount
                : currentQuantity - adjustmentAmount;

            if (newQuantity < 0) throw new Error("Insufficient inventory for the requested adjustment.");

            transaction.update(itemRef, {
                quantity: newQuantity,
                last_updated: new Date(),
            });

            const historyRef = collection(db, "organizations", organization, "inventory", itemId, "inventory_history");
            await addDoc(historyRef, {
                change_type: adjustmentType,
                old_value: currentQuantity,
                new_value: newQuantity,
                quantity_adjusted: adjustmentAmount,
                reason,
                notes,
                updated_by: userId,
                update_date: new Date(),
            });
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Transaction failed: ", error);
        return { success: false, error: error.message || 'Failed to adjust inventory item' };
    }
};

// Real-time listener for all inventory items
export const organizationInventorySnapshotListener = (organization, onInventoryUpdate) => {
    const inventoryQuery = query(collection(db, "organizations", organization, "inventory"),
    )
    const unsubscribe = onSnapshot(inventoryQuery, (querySnapshot) => {
        const inventoryItems = [];
        querySnapshot.forEach((doc) => {
          inventoryItems.push({ id: doc.id, ...doc.data() });
        });
        onInventoryUpdate(inventoryItems);
    })
    return () => unsubscribe()
}

// Log inventory history
export const logInventoryHistory = async (organization, itemId, changeType, oldValue, newValue, changedBy) => {
    try {
        const historyRef = collection(db, "organizations", organization, "inventory", itemId, "history");
        await addDoc(historyRef, {
            change_type: changeType,
            old_value: oldValue,
            new_value: newValue,
            changed_by: changedBy,
            change_date: new Date(),
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error logging inventory history:", error);
        return { success: false, error: error.message || 'Failed to log inventory history' };
    }
};